/**
 * Attendee session-token store (native apps).
 *
 * The web PWA authenticates with the HttpOnly `vc_attendee_session` cookie and
 * never uses this store (getStoredToken returns null → the request interceptor
 * adds no Authorization header, and the cookie carries the session).
 *
 * The native (Capacitor) app has no cookie jar, so it stores the attendee JWT
 * returned by `/attendee/verify` in secure storage (iOS Keychain / Android
 * Keystore) and sends it as `Authorization: Bearer`. Because the axios request
 * interceptor is synchronous, we keep an in-memory copy that is hydrated from
 * secure storage at app startup (see initAuthToken).
 *
 * This module deliberately has NO Capacitor import so it works in the plain web
 * build; the native secure-storage adapter is injected via `setTokenPersistor`
 * from the Capacitor entry point.
 */

let inMemoryToken: string | null = null

/** True only inside the Capacitor native shell (iOS/Android). */
function isNative(): boolean {
  const cap = (globalThis as { Capacitor?: { isNativePlatform?: () => boolean } })
    .Capacitor
  return Boolean(cap?.isNativePlatform?.())
}

/** Optional persistence hook, set by the native bootstrap (Keychain/Keystore). */
type Persistor = {
  save: (token: string) => void | Promise<void>
  clear: () => void | Promise<void>
}
let persistor: Persistor | null = null

/** Wire up native secure storage (called once from the Capacitor bootstrap). */
export function setTokenPersistor(p: Persistor): void {
  persistor = p
}

/** Hydrate the in-memory token at startup (native reads from secure storage). */
export function hydrateToken(token: string | null): void {
  inMemoryToken = token
}

/**
 * Synchronous read used by the axios request interceptor.
 *
 * WEB: always returns null — the web PWA authenticates with the HttpOnly session
 * cookie, so we must NOT attach a Bearer header (the backend prefers Bearer over
 * the cookie; a stale/lost in-memory token would then 401 requests the cookie
 * would have authenticated). NATIVE has no cookie jar, so the stored JWT is used.
 */
export function getStoredToken(): string | null {
  return isNative() ? inMemoryToken : null
}

/** Persist a freshly-issued session token (native only; no-op on web). */
export function setToken(token: string): void {
  if (!isNative()) return
  inMemoryToken = token
  void persistor?.save(token)
}

/** Clear the session (logout). */
export function clearToken(): void {
  inMemoryToken = null
  void persistor?.clear()
}
